<template>
  <div>
    <!-- Toast Notification -->
    <Transition name="toast">
      <div
        v-if="toast.visible"
        class="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg"
        :class="toast.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'">
        <svg
          v-if="toast.type === 'success'"
          class="w-5 h-5 text-green-500"
          fill="currentColor"
          viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <svg
          v-else
          class="w-5 h-5 text-red-500"
          fill="currentColor"
          viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <span
          class="text-sm font-medium"
          :class="toast.type === 'success' ? 'text-green-800' : 'text-red-800'">
          {{ toast.message }}
        </span>
      </div>
    </Transition>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <svg class="animate-spin w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <span class="ml-2 text-sm" style="color: var(--text-muted);">Loading websites...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="!websites.length" class="text-center py-8">
      <div
        class="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
        style="background-color: var(--bg-muted);">
        <svg class="w-6 h-6" style="color: var(--text-tertiary);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3" />
        </svg>
      </div>
      <p class="text-body-sm" style="color: var(--text-tertiary);">
        No websites yet
      </p>
      <p class="text-caption" style="color: var(--text-muted);">
        Create a website to configure uptime notifications.
      </p>
    </div>

    <!-- Websites List -->
    <div v-else class="space-y-4">
      <div
        v-for="website in websites"
        :key="website.id"
        class="p-4 rounded-lg border transition-opacity"
        :class="{ 'opacity-60 pointer-events-none': saving[website.id] }"
        style="border-color: var(--border-secondary); background-color: var(--bg-surface-sunken);">
        <div class="flex items-start justify-between mb-3">
          <div>
            <h4 class="text-body-sm font-semibold" style="color: var(--text-primary);">
              {{ website.business_name || 'Untitled Website' }}
            </h4>
            <p class="text-caption" style="color: var(--text-tertiary);">
              {{ website.live_url || website.preview_url || 'No URL set' }}
            </p>
          </div>
          <span
            v-if="website.uptime_enabled"
            class="badge badge-success badge-sm">
            Monitoring Active
          </span>
          <span v-else class="badge badge-default badge-sm">Not Monitored</span>
        </div>

        <!-- Notification Toggle -->
        <label
          class="flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors"
          :class="saving[website.id] ? 'cursor-not-allowed' : 'hover:bg-gray-50'"
          style="background-color: var(--bg-surface);">
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" style="color: var(--text-secondary);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div>
              <span class="text-body-sm font-medium" style="color: var(--text-primary);">Email Notifications</span>
              <span class="text-caption block" style="color: var(--text-tertiary);">Get notified when your site goes down</span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <svg
              v-if="saving[website.id]"
              class="animate-spin w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <input
              type="checkbox"
              class="form-checkbox"
              :checked="website.uptime_notifications_enabled"
              @change="toggleNotifications(website)"
              :disabled="saving[website.id]">
          </div>
        </label>

        <!-- Email Address for Notifications -->
        <div v-if="website.uptime_notifications_enabled" class="mt-3">
          <label class="form-label text-caption">Notification Email</label>
          <div class="flex gap-2">
            <div class="relative flex-1">
              <input
                type="email"
                class="form-input form-input-sm w-full transition-colors"
                :class="{
                  'border-green-300 bg-green-50': savedStates[website.id],
                  'opacity-50': saving[website.id]
                }"
                v-model="emailInputs[website.id]"
                placeholder="your@email.com"
                :disabled="saving[website.id]"
                @keydown.enter="saveEmail(website)">
              <Transition name="fade">
                <svg
                  v-if="savedStates[website.id]"
                  class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              </Transition>
            </div>
            <button
              type="button"
              class="btn btn-secondary btn-sm min-w-[70px] flex items-center justify-center"
              @click="saveEmail(website)"
              :disabled="saving[website.id] || !hasEmailChanged(website)">
              <svg v-if="saving[website.id]" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span v-else>Save</span>
            </button>
          </div>
          <p v-if="!hasEmailChanged(website) && emailInputs[website.id]" class="text-xs mt-1" style="color: var(--text-muted);">
            Email saved
          </p>
        </div>
      </div>

      <!-- Info Note -->
      <div class="p-3 rounded-lg" style="background-color: var(--bg-muted);">
        <div class="flex items-start gap-2">
          <svg class="w-5 h-5 flex-shrink-0 mt-0.5" style="color: var(--text-tertiary);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-caption" style="color: var(--text-secondary);">
            When enabled, you'll receive email alerts when your site goes down or recovers.
            Uptime monitoring checks your site every 5 minutes.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'

const loading = ref(true)
const websites = ref([])
const saving = reactive({})
const emailInputs = reactive({})
const originalEmails = reactive({})
const savedStates = reactive({})
const toast = reactive({
  visible: false,
  message: '',
  type: 'success'
})

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

const showToast = (message, type = 'success') => {
  toast.message = message
  toast.type = type
  toast.visible = true

  setTimeout(() => {
    toast.visible = false
  }, 3000)
}

const fetchWebsites = async() => {
  try {
    const response = await fetch('/ajax/profile/notification_settings', {
      headers: { 'Accept': 'application/json' }
    })

    if (response.ok) {
      const data = await response.json()
      websites.value = data.websites || []

      // Initialize email inputs and track original values
      websites.value.forEach((w) => {
        const email = w.uptime_notification_emails?.[0] || ''
        emailInputs[w.id] = email
        originalEmails[w.id] = email
      })
    }
  } catch (error) {
    console.error('[UptimeNotifications] Failed to fetch websites:', error)
    showToast('Failed to load websites', 'error')
  } finally {
    loading.value = false
  }
}

const hasEmailChanged = (website) => emailInputs[website.id] !== originalEmails[website.id]

const toggleNotifications = async(website) => {
  saving[website.id] = true

  try {
    const response = await fetch(`/ajax/websites/${website.id}/uptime_notifications`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        uptime_notifications_enabled: !website.uptime_notifications_enabled
      })
    })

    if (response.ok) {
      website.uptime_notifications_enabled = !website.uptime_notifications_enabled
      showToast(website.uptime_notifications_enabled ?
        'Notifications enabled' :
        'Notifications disabled')
    } else {
      showToast('Failed to update notifications', 'error')
    }
  } catch (error) {
    console.error('[UptimeNotifications] Toggle failed:', error)
    showToast('Failed to update notifications', 'error')
  } finally {
    saving[website.id] = false
  }
}

const saveEmail = async(website) => {
  const email = emailInputs[website.id]

  // Basic email validation
  if (email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    showToast('Please enter a valid email address', 'error')
    return
  }

  saving[website.id] = true

  try {
    const response = await fetch(`/ajax/websites/${website.id}/uptime_notifications`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        uptime_notification_emails: email ? [email] : []
      })
    })

    if (response.ok) {
      website.uptime_notification_emails = email ? [email] : []
      originalEmails[website.id] = email

      // Show saved checkmark briefly
      savedStates[website.id] = true
      setTimeout(() => {
        savedStates[website.id] = false
      }, 2000)

      showToast('Email saved successfully')
    } else {
      showToast('Failed to save email', 'error')
    }
  } catch (error) {
    console.error('[UptimeNotifications] Save email failed:', error)
    showToast('Failed to save email', 'error')
  } finally {
    saving[website.id] = false
  }
}

onMounted(() => {
  fetchWebsites()
})
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
